import {
  PRODUCT_GROUP_COUNT_URL,
  PRODUCT_GROUP_URL,
  PRODUCT_DETAILS_URL,
  PRODUCT_GROUP_LIST_URL,
  PRODUCT_TYPE_DETAIL_URL,
  PRODUCT_URL,
  PRODUCT_SEARCH_URL,
  PRODUCT_MODEL_SEARCH_URL,
  MANUFACTURER_SEARCH_URL
} from 'core/constants';
import { createApiRequest } from 'core/util';
import { UpdateProductBody } from 'core/types';
import {
  fetchProductGroupCountSuccess,
  fetchProductGroupCountError,
  fetchProductGroupStart,
  fetchProductGroupSuccess,
  fetchProductGroupError,
  fetchProductDetailsSuccess,
  fetchProductTypeGroupsListSuccess,
  fetchProductTypeGroupsListError,
  fetchProductTemplateSuccess,
  fetchProductTemplateStart,
  fetchProductTemplateError,
  createProductStart,
  createProductSuccess,
  createProductError,
  fetchProductDetailsStart,
  fetchSearchedProductsError,
  fetchSearchedProductsStart,
  fetchSearchedProductsSuccess,
  updateProductStart,
  updateProductSuccess,
  updateProductError,
  deleteProductStart,
  deleteProductSuccess,
  deleteProductError,
  searchProductModelsStart,
  searchProductModelsSuccess,
  searchProductModelsError,
  searchManufacturersStart,
  searchManufacturersSuccess,
  searchManufacturersError,
  searchOcrProductModelsSuccess,
  searchOcrManufacturersSuccess
} from 'features/shared/inventory/store/InventorySlice';

export const requestProductGroupCount = (homeId: number) =>
  createApiRequest({
    route: PRODUCT_GROUP_COUNT_URL,
    replace: ['home_id', homeId],
    onSuccess: fetchProductGroupCountSuccess,
    onError: fetchProductGroupCountError,
    defaultErrorMessage: 'There was a problem loading your products.',
    useGlobalLoader: true
  })();

export const requestProductGroup = (homeId: number, groupId: string) =>
  createApiRequest({
    route: PRODUCT_GROUP_URL,
    replace: [
      ['home_id', homeId],
      ['group_id', groupId]
    ],
    onStart: fetchProductGroupStart,
    onSuccess: fetchProductGroupSuccess,
    onError: fetchProductGroupError,
    defaultErrorMessage: 'There was a problem loading your products.',
    useGlobalLoader: true
  })();

export const fetchProductDetails = (homeId: number, itemId: number) =>
  createApiRequest({
    route: PRODUCT_DETAILS_URL,
    replace: [
      ['home_id', homeId],
      ['item_id', itemId]
    ],
    onStart: fetchProductDetailsStart,
    onSuccess: fetchProductDetailsSuccess,
    defaultErrorMessage: 'There was a problem loading item details.',
    useGlobalLoader: true
  })();

export const fetchProductTypeGroups = () =>
  createApiRequest({
    route: PRODUCT_GROUP_LIST_URL,
    method: 'get',
    onSuccess: fetchProductTypeGroupsListSuccess,
    onError: fetchProductTypeGroupsListError
  })();

export const fetchProductTemplate = (productTypeId: number) =>
  createApiRequest({
    route: PRODUCT_TYPE_DETAIL_URL,
    method: 'get',
    replace: [['product_type_id', productTypeId]],
    onStart: fetchProductTemplateStart,
    onSuccess: fetchProductTemplateSuccess,
    onError: fetchProductTemplateError,
    defaultErrorMessage: 'There was a problem fetching your catalog tasks.'
  })();

export const createInventoryProduct = (homeId: number, body: object) =>
  createApiRequest({
    route: PRODUCT_URL,
    method: 'post',
    body: body,
    replace: [['home_id', homeId]],
    onStart: createProductStart,
    onSuccess: createProductSuccess,
    onError: createProductError,
    defaultErrorMessage: 'There was an error updating the product templates.'
  })();

export const fetchSearchedProducts = (homeId: number, term: string) =>
  createApiRequest({
    route: PRODUCT_SEARCH_URL,
    method: 'get',
    replace: [
      ['home_id', homeId],
      ['term', term]
    ],
    onStart: fetchSearchedProductsStart,
    onSuccess: fetchSearchedProductsSuccess,
    onError: fetchSearchedProductsError,
    defaultErrorMessage: 'There was a problem searching products.'
  })();

/**
 * Homeowner edit — PATCH `/home/{home_id}/product/{id}` with only the fields
 * that changed (serial, registered, and/or product_template_id). Group,
 * Product Type, and catalog brand/model text are not homeowner-editable here.
 */
export const updateProduct = (
  homeId: number,
  productId: number,
  body: UpdateProductBody
) =>
  createApiRequest({
    route: PRODUCT_DETAILS_URL,
    method: 'patch',
    body: body,
    replace: [
      ['home_id', homeId],
      ['item_id', productId]
    ],
    onStart: updateProductStart,
    onSuccess: updateProductSuccess,
    onError: updateProductError,
    defaultErrorMessage: 'There was a problem updating this product.'
  })();

/**
 * Homeowner delete — DELETE `/home/{home_id}/product/{id}` (soft delete,
 * already supported server-side). Reachable from the detail Delete Confirm modal.
 */
export const deleteProduct = (homeId: number, productId: number) =>
  createApiRequest({
    route: PRODUCT_DETAILS_URL,
    method: 'delete',
    replace: [
      ['home_id', homeId],
      ['item_id', productId]
    ],
    onStart: deleteProductStart,
    onSuccess: deleteProductSuccess,
    onError: deleteProductError,
    defaultErrorMessage: 'There was a problem deleting this product.'
  })();

/**
 * Homeowner-safe `product_templates` catalog search, powering the "Product
 * Model" step of the cascading Edit + Add Item select. Distinct from
 * `fetchSearchedProducts`, which searches the homeowner's own inventory, not
 * the catalog. The endpoint itself is not home-scoped — the catalog is global
 * — but `homeId` stays the leading param for backward compatibility with the
 * original call signature (kept purely additive; unused internally, callers
 * unaffected either way). Callers should debounce (~1s) and skip dispatching
 * while `term` is empty (unless narrowed via `typeId`/`manufacturerId`, which
 * alone can return a full list for that Type/Brand). `typeId`/`manufacturerId`
 * scope results to an already-chosen Group/Type/Brand — omit to search unscoped.
 */
export const searchProductModels = (
  homeId: number,
  term: string,
  typeId?: number,
  manufacturerId?: number
) =>
  createApiRequest({
    route: PRODUCT_MODEL_SEARCH_URL,
    method: 'get',
    params: {
      term: term || undefined,
      type_id: typeId,
      manufacturer_id: manufacturerId,
      per_page: 100
    },
    onStart: searchProductModelsStart,
    onSuccess: searchProductModelsSuccess,
    onError: searchProductModelsError,
    defaultErrorMessage: 'There was a problem searching product models.'
  })();

/**
 * Homeowner-safe `manufacturers` catalog browse, powering the cascading
 * Brand/Manufacturer select (Edit + Add Item). Not home-scoped — the catalog
 * is global. `typeId` narrows results to manufacturers with at least one real
 * model of that type — the admin equivalent (`/admin/manufacturers`) 401s a
 * homeowner JWT.
 */
export const searchManufacturers = (typeId?: number, term?: string) =>
  createApiRequest({
    route: MANUFACTURER_SEARCH_URL,
    method: 'get',
    params: {
      type_id: typeId,
      term: term || undefined,
      per_page: 100
    },
    onStart: searchManufacturersStart,
    onSuccess: searchManufacturersSuccess,
    onError: searchManufacturersError,
    defaultErrorMessage: 'There was a problem searching manufacturers.'
  })();

/**
 * OCR-only variants of the two catalog searches above, hitting the exact same
 * endpoints with the same params but landing their results in the dedicated
 * `ocrProductModels`/`ocrManufacturers` slots.
 *
 * Why not just reuse the thunks above: `resolveAnalyzedProductUpdate` searches
 * for one specific label string, and the shared `productModels`/`manufacturers`
 * fields are the live backing lists for the cascading Brand/Model selects.
 * Writing a single-term OCR result into them narrowed those dropdowns for every
 * other consumer — visibly so in Edit mode, whose manufacturer refetch is keyed
 * on `typeId` changing and therefore never re-widened the list for the same
 * product. See InventorySlice's `ocrProductModels` comment.
 */
export const searchProductModelsForOcr = (
  homeId: number,
  term: string,
  typeId?: number,
  manufacturerId?: number
) =>
  createApiRequest({
    route: PRODUCT_MODEL_SEARCH_URL,
    method: 'get',
    params: {
      term: term || undefined,
      type_id: typeId,
      manufacturer_id: manufacturerId,
      per_page: 100
    },
    onSuccess: searchOcrProductModelsSuccess,
    defaultErrorMessage: 'There was a problem searching product models.'
  })();

export const searchManufacturersForOcr = (typeId?: number, term?: string) =>
  createApiRequest({
    route: MANUFACTURER_SEARCH_URL,
    method: 'get',
    params: {
      type_id: typeId,
      term: term || undefined,
      per_page: 100
    },
    onSuccess: searchOcrManufacturersSuccess,
    defaultErrorMessage: 'There was a problem searching manufacturers.'
  })();
